import { useEffect, useState } from 'react';
import { phase10Api, type EquilibriumData } from '../api/phase10Client';
import { growthApi } from '../api/phase12Client';

interface Block {
  block_id: string;
  block_name: string;
  output_variable_id: string;
  value: number;
  trace: { label: string; value: number; note?: string }[];
}

export function EquilibriumPage() {
  const [data, setData] = useState<EquilibriumData | null>(null);
  const [constraints, setConstraints] = useState<any[]>([]);
  const [literature, setLiterature] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<{ mode: string; modes: string[]; descriptions: Record<string, string> } | null>(null);

  const load = () => {
    Promise.all([phase10Api.equilibrium(), phase10Api.constraints(), phase10Api.literature(), growthApi.constraintMode()])
      .then(([eq, c, lit, m]) => { setData(eq); setConstraints(c); setLiterature(lit); setMode(m); })
      .catch((e) => setError(String(e)));
  };
  useEffect(load, []);
  const changeMode = async (m: string) => { await growthApi.setConstraintMode(m); load(); };

  if (error) return <div className="error">{error}</div>;
  if (!data) return <p>Loading equilibrium chain…</p>;

  const capped = data.binding_constraints.length > 0;

  return (
    <div>
      <h1 className="page-title">Policy Equilibrium</h1>
      <p className="page-subtitle">
        Chained sub-models: Energy → Industry → Export → Macro/Fiscal. The BOP ceiling is advisory by default (reported, not applied); switch to cap or soft below.
      </p>

      {mode && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '4px solid #1E3A5F' }}>
          <h3>BOP constraint mode</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {mode.modes.map((m) => <button key={m} className={m === mode.mode ? 'btn-primary' : 'btn-secondary'} onClick={() => changeMode(m)}>{m}</button>)}
          </div>
          <div className="small">{mode.descriptions[mode.mode]}</div>
        </div>
      )}
      {(data as any).advisories?.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '4px solid #F59E0B' }}>
          <strong>External-balance advisory:</strong> {(data as any).advisories.join(' · ')}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>GDP Growth — Unconstrained vs Equilibrium</h3>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Unconstrained (linkage sum)</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>{data.unconstrained_gdp_growth.toFixed(2)}%</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Equilibrium (after constraints)</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600, color: capped ? '#dc2626' : '#16a34a' }}>
              {data.equilibrium_gdp_growth.toFixed(2)}%
            </div>
          </div>
        </div>
        {capped && (
          <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', borderRadius: 6 }}>
            <strong>Binding:</strong> {data.binding_constraints.join(' · ')}
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>Equilibrium Chain</h3>
        {data.blocks.map((b, i) => (
          <div key={b.block_id} style={{ marginBottom: '1rem', paddingLeft: i * 12, borderLeft: '3px solid #16a34a' }}>
            <strong>{i + 1}. {b.block_name}</strong>
            <span style={{ marginLeft: 8, color: '#6b7280' }}>
              {'→ '}{b.output_variable_id}: {b.value.toFixed(3)}
            </span>
            <ul style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
              {b.trace.map((t) => (
                <li key={t.label}>{t.label}: {typeof t.value === 'number' ? t.value.toFixed(3) : t.value}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>Active Constraints</h3>
        <table className="data-table">
          <thead><tr><th>Name</th><th>Type</th><th>Threshold</th><th>Evidence</th></tr></thead>
          <tbody>
            {constraints.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.constraint_type}</td>
                <td>{c.threshold_value}</td>
                <td>{c.evidence_id || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Literature Linkages</h3>
        <table className="data-table">
          <thead><tr><th>Source</th><th>Target</th><th>Estimate</th><th>Reference</th></tr></thead>
          <tbody>
            {literature.map((l) => (
              <tr key={l.id}>
                <td>{l.source_variable_id}</td>
                <td>{l.target_variable_id}</td>
                <td>{l.central_estimate} {l.units}</td>
                <td style={{ fontSize: '0.85rem' }}>{l.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
